import { useState } from 'react';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import FinancingBanner from '../../../components/feature/FinancingBanner';
import RecoveryPriceCalculator from './components/RecoveryPriceCalculator';

export default function RecoveryServicesPage() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    animalType: '',
    location: '',
    details: '',
    urgency: 'urgent'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    const formBody = new URLSearchParams();
    Object.entries(formData).forEach(([key, value]) => {
      formBody.append(key, value);
    });

    try {
      await fetch('https://readdy.ai/api/form/d63eneabdoed7v0v8nj0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });
      setSubmitSuccess(true);
      setFormData({
        name: '',
        phone: '',
        email: '',
        animalType: '',
        location: '',
        details: '',
        urgency: 'urgent'
      });
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative pt-24 pb-20 px-6">
        <div className="absolute inset-0 z-0">
          <img
            src="https://public.readdy.ai/ai/img_res/d1ba8030052a3beddc3a0c50bc215de8.jpg"
            alt="Drone recovery mission at dawn"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/40"></div>
        </div>
        
        <div className="relative z-10 max-w-6xl mx-auto pt-16">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Animal Recovery Services
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed">
              When an animal is missing, injured, or down, every minute matters. Our thermal imaging drones help locate livestock, pets, and wildlife quickly—often resolving in hours what could take days on foot.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="tel:+17179131780"
                className="inline-flex items-center justify-center bg-emerald-700 hover:bg-emerald-600 text-white font-bold px-8 py-4 rounded-lg transition-colors cursor-pointer whitespace-nowrap text-lg"
              >
                <i className="ri-phone-line text-xl mr-3"></i>
                Call Now: 717-913-1780
              </a>
              <a
                href="#urgent-form"
                className="inline-flex items-center justify-center bg-white/10 backdrop-blur-sm border-2 border-white/30 hover:bg-white/20 text-white font-bold px-8 py-4 rounded-lg transition-colors cursor-pointer whitespace-nowrap text-lg"
              >
                Submit Recovery Request
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="bg-emerald-800 py-6 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div>
              <div className="text-3xl font-bold text-white">24/7</div>
              <div className="text-emerald-200 text-sm">Emergency Response</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-white">90%+</div>
              <div className="text-emerald-200 text-sm">Recovery Success Rate</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-white">500+</div>
              <div className="text-emerald-200 text-sm">Animals Located</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-white">Mid-Atlantic</div>
              <div className="text-emerald-200 text-sm">Service Area</div>
            </div>
          </div>
        </div>
      </section>

      {/* Cost of Delay Callout */}
      <section className="py-10 px-6 bg-stone-100 border-y border-stone-200">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-red-600">
              <div className="flex items-start gap-3">
                <i className="ri-time-line text-2xl text-red-600 mt-0.5"></i>
                <div>
                  <div className="text-sm text-gray-600 mb-1">Every hour of delay</div>
                  <div className="text-2xl font-bold text-red-600">2x larger search area</div>
                  <p className="text-sm text-gray-600 mt-2">A lost animal can travel miles. The longer you wait, the harder recovery becomes.</p>
                </div>
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-red-600">
              <div className="flex items-start gap-3">
                <i className="ri-money-dollar-circle-line text-2xl text-red-600 mt-0.5"></i>
                <div>
                  <div className="text-sm text-gray-600 mb-1">Average livestock loss</div>
                  <div className="text-2xl font-bold text-red-600">$1,500–$3,000+</div>
                  <p className="text-sm text-gray-600 mt-2">Per head for cattle. Calves, breeding stock, and working animals often worth more.</p>
                </div>
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-emerald-600">
              <div className="flex items-start gap-3">
                <i className="ri-shield-check-line text-2xl text-emerald-700 mt-0.5"></i>
                <div>
                  <div className="text-sm text-gray-600 mb-1">We handle everything</div>
                  <div className="text-2xl font-bold text-emerald-700">End-to-end recovery</div>
                  <p className="text-sm text-gray-600 mt-2">From the moment you call to the moment your animal is located. We're your partner.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How Thermal Imaging Works */}
      <section className="py-20 px-6 bg-stone-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">How Thermal Drone Recovery Works</h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto leading-relaxed">
              Thermal imaging detects body heat, making animals visible even in dense cover, tall grass, or darkness. What the human eye can't see, our drones can find.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <h3 className="text-2xl font-bold mb-4">Normal View vs. Thermal View</h3>
              <p className="text-gray-700 leading-relaxed mb-6">
                In a standard camera view, an animal hiding in brush, tall grass, or wooded areas is nearly impossible to spot. Thermal imaging cuts through the visual clutter by detecting heat signatures—showing exactly where the animal is, regardless of cover.
              </p>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <i className="ri-check-line text-2xl text-emerald-800 mr-3 mt-0.5"></i>
                  <span>Detects body heat through vegetation and brush</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-2xl text-emerald-800 mr-3 mt-0.5"></i>
                  <span>Works in complete darkness and low-light conditions</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-2xl text-emerald-800 mr-3 mt-0.5"></i>
                  <span>Covers large areas in minutes, not hours</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-2xl text-emerald-800 mr-3 mt-0.5"></i>
                  <span>Pinpoints exact location for ground teams</span>
                </li>
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <img
                  src="https://readdy.ai/api/search-image?query=Aerial%20view%20of%20dense%20woodland%20and%20brush%20from%20drone%20camera%20in%20normal%20daylight%20RGB%20mode%2C%20thick%20vegetation%20covering%20forest%20floor%2C%20impossible%20to%20see%20any%20animals%20hidden%20below%2C%20natural%20green%20and%20brown%20earth%20tones%2C%20realistic%20drone%20photography%20perspective&width=400&height=500&seq=normal-view-001&orientation=portrait"
                  alt="Normal camera view - animal hidden"
                  className="w-full h-64 object-cover rounded-lg shadow-md"
                />
                <p className="text-center text-sm text-gray-600 mt-2 font-medium">Normal View</p>
              </div>
              <div>
                <img
                  src="https://readdy.ai/api/search-image?query=Thermal%20infrared%20drone%20camera%20view%20showing%20bright%20white%20heat%20signature%20of%20deer%20or%20large%20animal%20clearly%20visible%20against%20dark%20cool%20background%20of%20forest%20floor%20and%20vegetation%2C%20professional%20thermal%20imaging%20display%20with%20temperature%20gradient%2C%20realistic%20FLIR%20style%20thermal%20photography&width=400&height=500&seq=thermal-view-001&orientation=portrait"
                  alt="Thermal view - animal clearly visible"
                  className="w-full h-64 object-cover rounded-lg shadow-md"
                />
                <p className="text-center text-sm text-gray-600 mt-2 font-medium">Thermal View</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-8 rounded-lg shadow-md">
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-flight-takeoff-line text-3xl text-emerald-800"></i>
                </div>
                <h4 className="font-bold text-lg mb-2">1. Rapid Deployment</h4>
                <p className="text-gray-600 text-sm">We arrive on-site quickly and launch within minutes of assessing the search area.</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-scan-line text-3xl text-emerald-800"></i>
                </div>
                <h4 className="font-bold text-lg mb-2">2. Systematic Search</h4>
                <p className="text-gray-600 text-sm">Thermal cameras scan the area methodically, covering ground that would take hours on foot.</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-map-pin-line text-3xl text-emerald-800"></i>
                </div>
                <h4 className="font-bold text-lg mb-2">3. Precise Location</h4>
                <p className="text-gray-600 text-sm">Once located, we provide exact GPS coordinates so you can reach the animal directly.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Livestock Recovery */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block bg-emerald-100 text-emerald-800 px-4 py-1 rounded-full text-sm font-medium mb-4">
                Livestock Recovery
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Cattle, Calves, Goats & Sheep</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                When livestock go missing or a calf is down in a large pasture, time is critical. Searching on foot or by ATV can take hours—and in bad weather or rough terrain, you might not find them at all.
              </p>
              
              {/* Red Callout - Livestock Cost */}
              <div className="bg-red-50 border border-red-200 rounded-lg p-5 mb-6">
                <div className="flex items-start gap-3">
                  <i className="ri-error-warning-line text-xl text-red-600 mt-0.5"></i>
                  <div>
                    <p className="text-sm text-red-800 font-medium">
                      A single lost calf can mean <span className="font-bold text-red-600">$800–$1,200</span> in direct loss. Breeding stock or registered cattle: <span className="font-bold text-red-600">$3,000–$10,000+</span>.
                    </p>
                    <p className="text-sm text-gray-600 mt-2">We typically locate livestock within 30–60 minutes of arrival.</p>
                  </div>
                </div>
              </div>

              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Our thermal drones can scan hundreds of acres quickly, locating animals in tall grass, brush, ravines, or wooded areas where they'd otherwise be invisible.
              </p>
              <div className="bg-stone-50 p-6 rounded-lg mb-6">
                <h4 className="font-bold mb-3">Common Livestock Recovery Scenarios:</h4>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-line text-xl text-emerald-800 mr-2"></i>
                    <span>Newborn calves separated from the herd</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-line text-xl text-emerald-800 mr-2"></i>
                    <span>Cattle that have broken through fencing</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-line text-xl text-emerald-800 mr-2"></i>
                    <span>Injured or down animals in large pastures</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-line text-xl text-emerald-800 mr-2"></i>
                    <span>Goats or sheep that have wandered into brush</span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="space-y-4">
              <img
                src="https://readdy.ai/api/search-image?query=Thermal%20infrared%20aerial%20drone%20view%20showing%20heat%20signatures%20of%20cattle%20herd%20in%20large%20pasture%20at%20dusk%2C%20bright%20white%20warm%20bodies%20of%20cows%20clearly%20visible%20against%20cooler%20dark%20ground%2C%20one%20separated%20calf%20visible%20at%20distance%20from%20main%20herd%2C%20professional%20FLIR%20thermal%20imaging%20style%2C%20realistic%20livestock%20monitoring%20photography&width=700&height=400&seq=livestock-thermal-001&orientation=landscape"
                alt="Thermal view of cattle herd with separated calf"
                className="w-full h-56 object-cover rounded-lg shadow-md"
              />
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20drone%20operator%20with%20thermal%20imaging%20drone%20searching%20rural%20cattle%20pasture%20at%20dawn%2C%20rolling%20hills%20with%20scattered%20trees%20and%20brush%2C%20farmer%20standing%20nearby%20watching%20drone%20flight%2C%20realistic%20working%20farm%20scene%20with%20natural%20morning%20light%20and%20earth%20tones&width=700&height=400&seq=livestock-search-001&orientation=landscape"
                alt="Drone operator searching pasture for livestock"
                className="w-full h-56 object-cover rounded-lg shadow-md"
              />
              {/* Supporting damage image */}
              <div className="relative">
                <img
                  src="https://readdy.ai/api/search-image?query=Stressed%20farmer%20standing%20at%20broken%20fence%20line%20on%20cattle%20farm%20looking%20out%20over%20empty%20pasture%20where%20livestock%20escaped%2C%20damaged%20wooden%20fence%20posts%20and%20wire%2C%20rural%20farmland%20setting%20with%20overcast%20sky%2C%20realistic%20emotional%20scene%20showing%20concern%20and%20urgency&width=700&height=300&seq=livestock-stress-001&orientation=landscape"
                  alt="Farmer at broken fence where cattle escaped"
                  className="w-full h-44 object-cover rounded-lg shadow-md"
                />
                <div className="absolute bottom-3 left-3 bg-white/95 px-3 py-1.5 rounded text-sm">
                  <span className="text-gray-700">Every hour of delay expands the search area</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Topo Divider */}
      <div className="topo-divider"></div>

      {/* Missing Pet Recovery */}
      <section className="py-20 px-6 bg-stone-50">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1 space-y-4">
              <img
                src="https://readdy.ai/api/search-image?query=Thermal%20infrared%20drone%20camera%20view%20showing%20bright%20heat%20signature%20of%20lost%20dog%20hiding%20in%20dense%20brush%20and%20undergrowth%2C%20clear%20white%20warm%20body%20visible%20against%20dark%20cool%20vegetation%20background%2C%20realistic%20FLIR%20thermal%20imaging%20style%20for%20pet%20search%20and%20rescue&width=700&height=400&seq=pet-thermal-001&orientation=landscape"
                alt="Thermal view of lost dog in brush"
                className="w-full h-56 object-cover rounded-lg shadow-md"
              />
              <img
                src="https://readdy.ai/api/search-image?query=Worried%20pet%20owner%20standing%20with%20drone%20operator%20in%20rural%20wooded%20area%20during%20search%20for%20missing%20dog%2C%20natural%20daylight%20filtering%20through%20trees%2C%20realistic%20emotional%20scene%20of%20pet%20recovery%20mission%2C%20earth%20tones%20and%20green%20forest%20setting&width=700&height=400&seq=pet-search-001&orientation=landscape"
                alt="Pet owner with drone operator during search"
                className="w-full h-56 object-cover rounded-lg shadow-md"
              />
            </div>
            <div className="order-1 md:order-2">
              <div className="inline-block bg-emerald-100 text-emerald-800 px-4 py-1 rounded-full text-sm font-medium mb-4">
                Missing Pet Recovery
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Dogs, Cats & Working Animals</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                A missing pet is a family emergency. When your dog runs off during a storm, your cat escapes into unfamiliar territory, or a working dog goes missing on the job, the stress is overwhelming.
              </p>

              {/* Red Callout - Pet Time Window */}
              <div className="bg-red-50 border border-red-200 rounded-lg p-5 mb-6">
                <div className="flex items-start gap-3">
                  <i className="ri-alarm-warning-line text-xl text-red-600 mt-0.5"></i>
                  <div>
                    <p className="text-sm text-red-800 font-medium">
                      <span className="font-bold text-red-600">93% of lost dogs</span> are found within the first 24 hours—if searched effectively. After 48 hours, recovery rates drop significantly.
                    </p>
                    <p className="text-sm text-gray-600 mt-2">We respond same-day for urgent pet recovery requests.</p>
                  </div>
                </div>
              </div>

              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Thermal imaging lets us search areas that would be impossible to cover on foot—dense woods, tall grass, creek beds, and brush piles where frightened animals often hide.
              </p>
              <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
                <h4 className="font-bold mb-3">Why Aerial Search Works for Pets:</h4>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <i className="ri-check-line text-xl text-emerald-800 mr-2"></i>
                    <span>Scared animals often hide and won't respond to calls</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-xl text-emerald-800 mr-2"></i>
                    <span>Thermal detects body heat even when animal is motionless</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-xl text-emerald-800 mr-2"></i>
                    <span>Covers large search areas before the animal moves further</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-xl text-emerald-800 mr-2"></i>
                    <span>Works at night when many lost pets are more active</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Deer & Wildlife Recovery */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block bg-emerald-100 text-emerald-800 px-4 py-1 rounded-full text-sm font-medium mb-4">
                Deer & Wildlife Recovery
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Post-Hit, Wounded & Lost Animals</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                After a vehicle collision or hunting situation, locating a wounded deer quickly is both ethical and practical. An injured animal can travel far into dense cover, making ground tracking extremely difficult.
              </p>

              {/* Red Callout - Wildlife Recovery Window */}
              <div className="bg-red-50 border border-red-200 rounded-lg p-5 mb-6">
                <div className="flex items-start gap-3">
                  <i className="ri-timer-line text-xl text-red-600 mt-0.5"></i>
                  <div>
                    <p className="text-sm text-red-800 font-medium">
                      Wounded deer can travel <span className="font-bold text-red-600">up to 1 mile</span> before bedding down. After <span className="font-bold text-red-600">6–8 hours</span>, body temperature drops and thermal detection becomes harder.
                    </p>
                    <p className="text-sm text-gray-600 mt-2">Early response dramatically improves recovery success.</p>
                  </div>
                </div>
              </div>

              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Our thermal drones can locate deer and other wildlife in thick brush, creek bottoms, and wooded areas—often finding animals that would otherwise never be recovered.
              </p>
              <div className="bg-stone-50 p-6 rounded-lg mb-6">
                <h4 className="font-bold mb-3">Wildlife Recovery Situations:</h4>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-line text-xl text-emerald-800 mr-2"></i>
                    <span>Deer struck by vehicles on rural roads</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-line text-xl text-emerald-800 mr-2"></i>
                    <span>Wounded deer after unsuccessful tracking</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-line text-xl text-emerald-800 mr-2"></i>
                    <span>Wildlife that has wandered into populated areas</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-line text-xl text-emerald-800 mr-2"></i>
                    <span>Injured animals reported by landowners</span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="space-y-4">
              <img
                src="https://readdy.ai/api/search-image?query=Thermal%20infrared%20aerial%20view%20showing%20heat%20signature%20of%20wounded%20deer%20bedded%20down%20in%20dense%20forest%20undergrowth%2C%20bright%20white%20warm%20body%20clearly%20visible%20against%20dark%20cool%20woodland%20floor%2C%20realistic%20FLIR%20thermal%20imaging%20for%20wildlife%20recovery%20and%20deer%20tracking&width=700&height=400&seq=deer-thermal-001&orientation=landscape"
                alt="Thermal view of deer in dense cover"
                className="w-full h-56 object-cover rounded-lg shadow-md"
              />
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20drone%20with%20thermal%20camera%20flying%20low%20over%20wooded%20rural%20area%20during%20deer%20recovery%20mission%2C%20dense%20forest%20and%20brush%20below%2C%20natural%20daylight%20with%20earth%20tones%2C%20realistic%20wildlife%20search%20and%20rescue%20drone%20operation%20photography&width=700&height=400&seq=deer-search-001&orientation=landscape"
                alt="Drone searching wooded area for deer"
                className="w-full h-56 object-cover rounded-lg shadow-md"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Topo Divider */}
      <div className="topo-divider"></div>

      {/* Why Aerial Recovery */}
      <section className="py-20 px-6 bg-stone-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Why Aerial Recovery Is Faster & More Effective</h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto leading-relaxed">
              Traditional ground searches are slow, exhausting, and often unsuccessful. Thermal drone recovery changes the equation entirely.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="bg-white p-8 rounded-lg shadow-md">
              <h3 className="text-xl font-bold mb-4 text-red-700">Traditional Ground Search</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <i className="ri-close-line text-xl text-red-600 mr-3"></i>
                  <span>Hours or days of walking through difficult terrain</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-close-line text-xl text-red-600 mr-3"></i>
                  <span>Limited visibility in brush, tall grass, and woods</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-close-line text-xl text-red-600 mr-3"></i>
                  <span>Impossible to search effectively at night</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-close-line text-xl text-red-600 mr-3"></i>
                  <span>Exhausting for searchers, stressful for owners</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-close-line text-xl text-red-600 mr-3"></i>
                  <span>Animal may move while you search the wrong area</span>
                </li>
              </ul>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-md border-2 border-emerald-200">
              <h3 className="text-xl font-bold mb-4 text-emerald-800">Thermal Drone Recovery</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <i className="ri-check-line text-xl text-emerald-700 mr-3"></i>
                  <span>Cover large areas in minutes, not hours</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-xl text-emerald-700 mr-3"></i>
                  <span>See through vegetation, brush, and cover</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-xl text-emerald-700 mr-3"></i>
                  <span>Equally effective day or night</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-xl text-emerald-700 mr-3"></i>
                  <span>Reduces stress with faster resolution</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-xl text-emerald-700 mr-3"></i>
                  <span>Pinpoint GPS location for direct recovery</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Cost of Inaction Summary */}
          <div className="bg-white border border-stone-200 rounded-lg p-8 mb-12">
            <h3 className="text-xl font-bold mb-6 text-center">The Real Cost of Waiting</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center p-4">
                <div className="text-3xl font-bold text-red-600 mb-2">$1,500+</div>
                <div className="text-sm text-gray-600">Average value of lost livestock per head</div>
              </div>
              <div className="text-center p-4 border-x border-stone-200">
                <div className="text-3xl font-bold text-red-600 mb-2">8–12 hrs</div>
                <div className="text-sm text-gray-600">Typical ground search time for 100 acres</div>
              </div>
              <div className="text-center p-4">
                <div className="text-3xl font-bold text-red-600 mb-2">50%</div>
                <div className="text-sm text-gray-600">Drop in recovery odds after 48 hours</div>
              </div>
            </div>
            <div className="mt-6 pt-6 border-t border-stone-200 text-center">
              <p className="text-gray-700">
                <span className="font-medium">Our average response:</span> On-site within hours. Animal located in under 60 minutes.
              </p>
            </div>
          </div>

          <div className="bg-emerald-800 text-white p-8 rounded-lg">
            <div className="grid md:grid-cols-4 gap-6 text-center">
              <div>
                <div className="text-4xl font-bold mb-2">10x</div>
                <div className="text-emerald-200">Faster Than Ground Search</div>
              </div>
              <div>
                <div className="text-4xl font-bold mb-2">100+</div>
                <div className="text-emerald-200">Acres Per Hour</div>
              </div>
              <div>
                <div className="text-4xl font-bold mb-2">24/7</div>
                <div className="text-emerald-200">Day or Night Capability</div>
              </div>
              <div>
                <div className="text-4xl font-bold mb-2">GPS</div>
                <div className="text-emerald-200">Exact Location Provided</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-stone-50 p-10 rounded-lg">
            <div className="flex items-start mb-6">
              <i className="ri-double-quotes-l text-5xl text-emerald-300 mr-4"></i>
            </div>
            <p className="text-xl text-gray-700 leading-relaxed mb-6 italic">
              "Our calf had been missing for almost a full day. We'd searched everywhere we could think of on foot and by ATV. The drone found her in less than 20 minutes—she was down in a ravine we never would have checked. I don't know what we would have done without this service."
            </p>
            <div className="flex items-center">
              <div className="w-12 h-12 bg-emerald-200 rounded-full flex items-center justify-center mr-4">
                <i className="ri-user-line text-xl text-emerald-800"></i>
              </div>
              <div>
                <div className="font-bold text-gray-900">Robert M.</div>
                <div className="text-gray-600 text-sm">Cattle Farmer, Lancaster County, PA</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Financing Section */}
      <FinancingBanner context="Emergency recovery shouldn't be a financial burden. We offer flexible payment options so you can focus on getting your animal back — not worrying about the bill." />

      {/* Topo Divider */}
      <div className="topo-divider"></div>

      {/* Service Overview & Value Proposition */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Why Experience Matters in Recovery</h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto leading-relaxed">
              Most people don't think about recovery services until they need one. When they do, they want the highest chance of success—not the lowest bid.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="bg-stone-50 p-8 rounded-lg">
              <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-shield-check-line text-2xl text-emerald-800"></i>
              </div>
              <h3 className="text-xl font-bold mb-3">90% Success Rate</h3>
              <p className="text-gray-700 leading-relaxed">
                Our recovery success rate exceeds typical outcomes due to proprietary search techniques, advanced thermal technology, and AI-assisted pattern recognition.
              </p>
            </div>
            <div className="bg-stone-50 p-8 rounded-lg">
              <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-time-line text-2xl text-emerald-800"></i>
              </div>
              <h3 className="text-xl font-bold mb-3">Speed Is Critical</h3>
              <p className="text-gray-700 leading-relaxed">
                Every hour of delay doubles the search area and reduces recovery probability. Our rapid response protocols ensure we're on-site when it matters most.
              </p>
            </div>
            <div className="bg-stone-50 p-8 rounded-lg">
              <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-tools-line text-2xl text-emerald-800"></i>
              </div>
              <h3 className="text-xl font-bold mb-3">Specialized Training</h3>
              <p className="text-gray-700 leading-relaxed">
                Not all drone operators are equipped or trained for recovery work. Our team uses methods and technology specifically designed for time-sensitive animal location.
              </p>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-8">
            <div className="flex items-start gap-4">
              <i className="ri-information-line text-3xl text-blue-600 mt-1"></i>
              <div>
                <h4 className="font-bold text-lg text-gray-900 mb-3">Recovery Is Not a Commodity Service</h4>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Recovery outcomes depend on speed, technique, and experience. Inexperienced operators waste critical time using ineffective search patterns. Delays reduce recovery probability and expand the search area exponentially.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  <strong>You often only get one real window to recover successfully.</strong> Our methods, training, and technology materially improve outcomes compared to standard aerial search approaches.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Recovery Price Calculator */}
      <RecoveryPriceCalculator />

      {/* Topo Divider */}
      <div className="topo-divider"></div>

      {/* Urgent Contact Form */}
      <section id="urgent-form" className="py-20 px-6 bg-stone-100">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Need Help Now?</h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              For urgent recovery needs, call us directly at <a href="tel:+17179131780" className="text-emerald-800 font-bold hover:underline cursor-pointer">717-913-1780</a>. Or submit the form below and we'll respond as quickly as possible.
            </p>
          </div>

          {submitSuccess ? (
            <div className="bg-emerald-50 border-2 border-emerald-200 rounded-lg p-8 text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-check-line text-3xl text-emerald-700"></i>
              </div>
              <h3 className="text-2xl font-bold text-emerald-800 mb-2">Request Received</h3>
              <p className="text-gray-700">We've received your recovery request and will contact you as soon as possible. For immediate assistance, please call <a href="tel:+17179131780" className="text-emerald-800 font-bold hover:underline cursor-pointer">717-913-1780</a>.</p>
            </div>
          ) : (
            <form 
              id="recovery-urgent-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="bg-white p-8 rounded-lg shadow-md"
            >
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Your Name *</label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                    placeholder="Full name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                    placeholder="Best number to reach you"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                    placeholder="your@email.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Type of Animal *</label>
                  <select
                    name="animalType"
                    required
                    value={formData.animalType}
                    onChange={(e) => setFormData({...formData, animalType: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm cursor-pointer"
                  >
                    <option value="">Select animal type</option>
                    <option value="cattle">Cattle / Calf</option>
                    <option value="goat-sheep">Goat / Sheep</option>
                    <option value="dog">Dog</option>
                    <option value="cat">Cat</option>
                    <option value="deer">Deer</option>
                    <option value="other-livestock">Other Livestock</option>
                    <option value="other-wildlife">Other Wildlife</option>
                  </select>
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Location / Address *</label>
                <input
                  type="text"
                  name="location"
                  required
                  value={formData.location}
                  onChange={(e) => setFormData({...formData, location: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                  placeholder="Address or general location where animal was last seen"
                />
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Details</label>
                <textarea
                  name="details"
                  rows={4}
                  maxLength={500}
                  value={formData.details}
                  onChange={(e) => setFormData({...formData, details: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm resize-none"
                  placeholder="Any details that might help: when last seen, description, terrain, etc."
                ></textarea>
                <p className="text-xs text-gray-500 mt-1">{formData.details.length}/500 characters</p>
              </div>

              <div className="mb-8">
                <label className="block text-sm font-medium text-gray-700 mb-2">Urgency Level</label>
                <div className="flex gap-4">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="urgency"
                      value="urgent"
                      checked={formData.urgency === 'urgent'}
                      onChange={(e) => setFormData({...formData, urgency: e.target.value})}
                      className="mr-2 text-emerald-600 focus:ring-emerald-500"
                    />
                    <span className="text-sm">Urgent - Need help ASAP</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="urgency"
                      value="standard"
                      checked={formData.urgency === 'standard'}
                      onChange={(e) => setFormData({...formData, urgency: e.target.value})}
                      className="mr-2 text-emerald-600 focus:ring-emerald-500"
                    />
                    <span className="text-sm">Standard - Within 24-48 hours</span>
                  </label>
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-emerald-800 hover:bg-emerald-700 disabled:bg-emerald-400 text-white font-bold py-4 px-8 rounded-lg transition-colors cursor-pointer whitespace-nowrap text-lg"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Recovery Request'}
              </button>
            </form>
          )}

          <div className="mt-8 text-center">
            <p className="text-gray-600 mb-4">For fastest response on urgent situations:</p>
            <a
              href="tel:+17179131780"
              className="inline-flex items-center justify-center bg-emerald-800 hover:bg-emerald-700 text-white font-bold px-8 py-4 rounded-lg transition-colors cursor-pointer whitespace-nowrap text-lg"
            >
              <i className="ri-phone-line text-xl mr-3"></i>
              Call 717-913-1780
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
